import React, { useEffect, useRef } from 'react';
import { tv } from '@/utils/tv';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import {
  GALLERY_PREVIEW_THUMBNAIL_SIZE,
  GalleryThumbnail,
} from '@/pages/DetailedNews/GalleryThumbnail';
import type { GalleryImageItem } from '@/types/gallery';

const SCROLL_STEP_RATIO = 0.8;

const thumbnail = tv({
  base: 'h-[124px] w-[140px] shrink-0 transition-[width,height] duration-200 ease-out',
  variants: {
    isActive: {
      true: 'h-[148px] w-[196px] border-[3px] border-accent',
    },
  },
});

interface Props {
  images: GalleryImageItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

export const GalleryFilmstrip: React.FC<Props> = ({
  images,
  activeIndex,
  onSelect,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollByStep = (direction: number) => {
    const strip = scrollRef.current;

    if (!strip) {
      return;
    }

    strip.scrollBy({
      left: direction * strip.clientWidth * SCROLL_STEP_RATIO,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const strip = scrollRef.current;

    if (!strip) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      const isHorizontalGesture =
        Math.abs(event.deltaX) > Math.abs(event.deltaY);

      if (isHorizontalGesture) {
        return;
      }

      event.preventDefault();
      strip.scrollLeft += event.deltaY;
    };

    strip.addEventListener('wheel', handleWheel, { passive: false });

    return () => strip.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const strip = scrollRef.current;
    const activeThumbnail = strip?.children[activeIndex] as
      | HTMLElement
      | undefined;

    if (!strip || !activeThumbnail) {
      return;
    }

    strip.scrollTo({
      left:
        activeThumbnail.offsetLeft -
        (strip.clientWidth - activeThumbnail.clientWidth) / 2,
      behavior: 'smooth',
    });
  }, [activeIndex]);

  return (
    <div className="hidden h-[148px] shrink-0 items-center gap-5 md:flex">
      <button
        type="button"
        aria-label="Scroll previews left"
        onClick={() => scrollByStep(-1)}
        className="flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-[transform,background-color] duration-150 ease-out hover:bg-secondary-hover active:scale-90 active:bg-secondary-pressed"
      >
        <ChevronLeft className="size-5" />
      </button>

      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollRef}
          className="scrollbar-hidden flex h-full items-center gap-4 overflow-x-auto"
        >
          {images.map((image, index) => (
            <GalleryThumbnail
              key={image.id}
              size={GALLERY_PREVIEW_THUMBNAIL_SIZE}
              className={thumbnail({ isActive: index === activeIndex })}
              image={image}
              onClick={() => onSelect(index)}
            />
          ))}
        </div>
        <span className="pointer-events-none absolute inset-y-0 left-0 w-[97px] bg-gradient-to-r from-card to-transparent" />
        <span className="pointer-events-none absolute inset-y-0 right-0 w-[97px] bg-gradient-to-l from-card to-transparent" />
      </div>

      <button
        type="button"
        aria-label="Scroll previews right"
        onClick={() => scrollByStep(1)}
        className="flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-[transform,background-color] duration-150 ease-out hover:bg-secondary-hover active:scale-90 active:bg-secondary-pressed"
      >
        <ChevronRight className="size-5" />
      </button>
    </div>
  );
};
